import { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { useParams, useHistory } from 'react-router-dom';
import { typography, color } from '@uniquegood/realworld-studio-design';
import { useRequest, useRequestImmutable } from '@/hooks';
import { adminApi } from '@/api';
import TrialPageHeader from './components/TrialPageHeader';
import ChannelItem from '../checkout/selectChannel/components/ChannelItem';
import { Channel } from '../checkout/selectChannel/types';

const PLAN_NAME_MAP: Record<string, string> = {
  Pro: '프로',
  Enterprise: '엔터프라이즈',
  Class: '클래스',
  Teacher: '티쳐'
};

interface TrialValidation {
  isValid: boolean;
  planType?: PlanType;
  useMonths?: number;
  invalidReason?: string;
}

function convertAppToChannel(app: ApplicationResponseModel, currentUserId?: string): Channel {
  const isOwner = app.members?.some(
    (member) => member.id && member.id === currentUserId && member.level === 'Owner'
  );

  return {
    id: app.id ?? '',
    name: app.name ?? '',
    description: app.description ?? '',
    avatarUrl: app.imageUrl,
    isOwner: isOwner ?? false,
    plan: app.planType,
    usePurposeType: app.usePurposeType ?? null
  };
}

const TrialPage = () => {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validation, setValidation] = useState<TrialValidation | null>(null);
  const [isValidating, setIsValidating] = useState(true);

  // Validate token
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await adminApi.get<{ data: TrialValidation }>(
          `/api/trial-links/${token}/validate`
        );
        setValidation(response.data.data);
      } catch (error) {
        setValidation({ isValid: false, invalidReason: '체험 링크 검증에 실패했습니다.' });
      } finally {
        setIsValidating(false);
      }
    };
    validateToken();
  }, [token]);

  // Fetch user data
  const { data: { data: apps } = {} } = useRequestImmutable<
    CommonResponseModel<ApplicationResponseModel[]>
  >('/apps', undefined, {
    headers: {
      'x-rwd-api-version': '1.1'
    }
  });
  const { data: me } = useRequest<MyInfo>('/api/me');

  const channels = useMemo(() => {
    if (!apps || !me) return [];
    return apps.map((app) => convertAppToChannel(app, me.id));
  }, [apps, me]);

  const isLoading = isValidating || !apps || !me;

  const planName = validation?.planType
    ? PLAN_NAME_MAP[validation.planType] || validation.planType
    : '';

  const handleSelectChannel = (channelId: string) => {
    setSelectedChannelId(channelId);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!selectedChannelId || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await adminApi.post(
        `/api/trial-links/${token}/redeem`,
        {
          applicationId: selectedChannelId
        },
        {
          skipGlobalErrorToast: true
        }
      );

      history.push({
        pathname: `/apps/${selectedChannelId}`,
        state: { trialRedeemSuccess: true }
      });
    } catch (error: any) {
      setIsSubmitting(false);
      const errorMessage = error?.response?.data?.message || '체험 적용 중 오류가 발생했습니다.';
      setSubmitError(errorMessage);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingText>체험 링크를 확인하고 있어요...</LoadingText>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // Invalid token
  if (validation && !validation.isValid) {
    return (
      <PageContainer>
        <ContentWrapper>
          <InvalidContainer>
            <InvalidIcon>!</InvalidIcon>
            <InvalidTitle>체험 링크를 사용할 수 없어요</InvalidTitle>
            <InvalidMessage>{validation.invalidReason}</InvalidMessage>
          </InvalidContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const selectableChannels = channels.filter(
    (channel) => channel.isOwner && channel.plan === 'Free'
  );
  const hasSelectableChannels = selectableChannels.length > 0;

  return (
    <PageContainer>
      <ContentWrapper>
        <TrialPageHeader
          planName={planName}
          useMonths={validation?.useMonths ?? 1}
          userName={me?.name || ''}
        />

        <FormWrapper>
          <ChannelList>
            {channels.map((channel) => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                isSelected={selectedChannelId === channel.id}
                onSelect={handleSelectChannel}
              />
            ))}
          </ChannelList>

          {!hasSelectableChannels && (
            <EmptyMessage>
              선택 가능한 채널이 없습니다. 새로운 채널을 생성하거나 채널 소유자 권한을 확인해주세요.
            </EmptyMessage>
          )}

          {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

          <SubmitButton onClick={handleSubmit} disabled={!selectedChannelId || isSubmitting}>
            {isSubmitting ? '체험 적용 중...' : '무료 체험 시작하기'}
          </SubmitButton>
        </FormWrapper>
      </ContentWrapper>
    </PageContainer>
  );
};

export default TrialPage;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 56px);
  background: #ffffff;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 100%;
  padding: 24px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 320px;
  max-width: 100%;
`;

const ChannelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #b0b0b0;
  }
`;

const LoadingText = styled.p`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #8f8f8f;
  text-align: center;
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #8f8f8f;
  text-align: center;
  padding: 24px;
`;

const ErrorMessage = styled.div`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${color.palette_status_red_tint_04};
  color: ${color.palette_status_red_100};
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  text-align: center;
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  width: 100%;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.medium};
  background: ${({ disabled }) => (disabled ? '#e8e8e8' : '#7800ff')};
  color: ${({ disabled }) => (disabled ? '#adadad' : '#ffffff')};
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

const InvalidContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 24px;
  text-align: center;
`;

const InvalidIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${color.palette_status_red_tint_04};
  color: ${color.palette_status_red_100};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: ${typography.weight.bold};
`;

const InvalidTitle = styled.h2`
  font-size: 20px;
  line-height: 28px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  margin: 0;
`;

const InvalidMessage = styled.p`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #8f8f8f;
  margin: 0;
`;
